import { useState, useEffect } from "react";
import "../css/UnitList.css";
import * as XLSX from "xlsx";

const SaleReportNew = () => {
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
        }
      } catch (error) {
        showToast("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, [API_URL]);

  // Move fetchHistory outside useEffect so it can be called directly
  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        ...(selectedProductId && { product_id: selectedProductId }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await fetch(`${API_URL}stock-sales-history.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      if (data.status && Array.isArray(data.sales)) {
        setHistory(data.sales);
      } else if (data.status && Array.isArray(data.data)) {
        // Fallback for purchase data
        setHistory(data.data);
      } else {
        setHistory([]);
        showToast(data.message || "No history found.", "info");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [selectedProductId, API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Filter history by date range on the frontend
  function filterByDateRange(data, startDate, endDate) {
    if (!startDate && !endDate) return data;
    return data.filter(item => {
      const itemDate = item.create_date ? item.create_date.slice(0, 10) : '';
      if (startDate && endDate) {
        return itemDate >= startDate && itemDate <= endDate;
      } else if (startDate) {
        return itemDate >= startDate;
      } else if (endDate) {
        return itemDate <= endDate;
      }
      return true;
    });
  }

  const filteredHistory = filterByDateRange(history, startDate, endDate);

  // Export to Excel function
  const exportToExcel = () => {
    if (!filteredHistory.length) return;
    const worksheetData = filteredHistory.map(item => ({
      "Product Name": item.product_name,
      "Date": new Date(item.create_date).toLocaleString(),
      "Type": item.type,
      "Quantity": `${item.quantity} ${item.unit_name}`,
      "Unit Price": `₹${item.per_unit_price}`,
      "Total Amount": `₹${item.total_amt}`,
    }));
    // Calculate total amount
    const totalAmount = filteredHistory.reduce((sum, item) => sum + parseFloat(item.total_amt || 0), 0);
    worksheetData.push({
      "Product Name": "Total",
      "Date": "",
      "Type": "",
      "Quantity": "",
      "Unit Price": "",
      "Total Amount": `₹${totalAmount}`,
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");
    XLSX.writeFile(workbook, "sales_report.xlsx");
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Sales Report</h1>
          <p>View all sales</p>
        </div>
      </div>

      <div className="filters-section" style={{ marginBottom: "30px", display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div className="form-group">
            <label htmlFor="product-filter">Filter by Product</label>
            <select
              id="product-filter"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.product}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="start-date">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary "
            onClick={() => {
              setSelectedProductId("");
              setStartDate("");
              setEndDate("");
            }}
            style={{ height: 40, alignSelf: 'flex-end', backgroundColor: 'red', color: 'white', marginLeft: 16, marginBottom: 10 }}
          >
            Reset
          </button>
        </div>
        <button className="btn btn-primary" onClick={exportToExcel} disabled={!history.length}>
          Export
        </button>
      </div>

      {isLoading ? (
        <p>Loading history...</p>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Date</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product_name}</td>
                    <td>{new Date(item.create_date).toLocaleString()}</td>
                    <td>
                      <span
                        className={`status-badge ${item.type.toLowerCase()}`}
                      >
                        {item.type}
                      </span>
                    </td>
                    <td>
                      {item.quantity} {item.unit_name}
                    </td>
                    <td>₹{item.per_unit_price}</td>
                    <td>₹{item.total_amt}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No records found.</td>
                </tr>
              )}
              {/* Total row */}
              {filteredHistory.length > 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'right' }}><b>Total</b></td>
                  <td style={{ backgroundColor: '#ffe066', fontWeight: 'bold' }}>
                    ₹ {filteredHistory.reduce((sum, item) => sum + parseFloat(item.total_amt || 0), 0)} 
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleReportNew;
